import React from 'react';
import { motion } from 'framer-motion';
import { Store, LayoutDashboard, ArrowRight, Sparkles, CheckCircle2, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Product shop = existing premium marketplace shop. Business shop = business
// landing page (reservations / digital menu / business card). Used both on the
// light Profile page and in the dark onboarding flow.
// `status` marks which type the user already has active, e.g. { product: true }.
export default function ShopTypeChooser({ theme = 'light', onPick, status = {}, paused = {}, className = '' }) {
  const { language } = useLanguage();
  const L = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? (ru ?? en) : en);
  const dark = theme === 'dark';
  const fmtDM = (iso) => { if (!iso || iso === true) return ''; const d = new Date(iso); return isNaN(d) ? '' : `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`; };
  const pausedLabel = (until) => {
    const dm = fmtDM(until);
    if (language === 'lv') return dm ? `Pauzēts līdz ${dm}` : 'Pauzēts';
    if (language === 'ru') return dm ? `Приостановлен до ${dm}` : 'Приостановлен';
    return dm ? `Paused until ${dm}` : 'Paused';
  };

  const options = [
    {
      id: 'product',
      icon: Store,
      title: L('Produktu veikals', 'Product shop', 'Магазин товаров'),
      desc: L('Tirgo fiziskas vai digitālas preces marketplace.', 'Sell physical or digital products on the marketplace.', 'Продавайте физические или цифровые товары на маркетплейсе.'),
      note: L('Pieejams ar Premium plāniem', 'Available with Premium plans', 'Доступно с Premium планами'),
      testid: 'shop-type-product',
    },
    {
      id: 'business',
      icon: LayoutDashboard,
      title: L('Biznesa lapa', 'Business page', 'Бизнес-страница'),
      desc: L('Rezervācijas, digitālā ēdienkarte un vizītkarte tavam uzņēmumam.', 'Reservations, digital menu and a business card for your company.', 'Бронирование, цифровое меню и визитка для вашей компании.'),
      note: L('Bezmaksas', 'Free', 'Бесплатно'),
      testid: 'shop-type-business',
    },
  ];

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 w-full ${className}`}>
      {options.map((o, i) => {
        const Icon = o.icon;
        const active = !!status[o.id];
        const pausedVal = paused[o.id];
        const isPaused = active && !!pausedVal;
        return (
          <motion.button
            key={o.id}
            type="button"
            onClick={() => onPick && onPick(o.id, active)}
            data-testid={o.testid}
            data-active={active ? 'true' : 'false'}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
            whileTap={{ scale: 0.98 }}
            className={
              dark
                ? `relative flex flex-col p-6 rounded-[24px] border transition-colors cursor-pointer group text-left ${active ? 'border-emerald-400/60 bg-emerald-400/10' : 'border-white/10 bg-white/5 hover:bg-white/[0.09] hover:border-amber-500/50'}`
                : `relative flex flex-col p-6 md:p-8 rounded-[24px] border-2 transition-all cursor-pointer group text-left ${active ? 'border-[#4A6B53] bg-[#F3F8F4] shadow-sm' : 'border-gray-100 bg-white hover:border-[#D97736] hover:shadow-lg hover:-translate-y-1'}`
            }
          >
            {active && (
              isPaused ? (
                <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-500 text-white" data-testid={`shop-type-paused-${o.id}`}>
                  <Clock className="w-3 h-3" /> {pausedLabel(pausedVal)}
                </span>
              ) : (
                <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#4A6B53] text-white" data-testid={`shop-type-active-${o.id}`}>
                  <CheckCircle2 className="w-3 h-3" /> {L('Aktīvs', 'Active', 'Активно')}
                </span>
              )
            )}
            <span className={
              dark
                ? `w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform ${active ? 'bg-emerald-400/20 text-emerald-300' : 'bg-amber-500/15 text-amber-400'}`
                : `w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform ${active ? 'bg-[#4A6B53]/15 text-[#4A6B53]' : 'bg-[#FFF6F0] text-[#D97736]'}`
            }>
              <Icon className="w-7 h-7" />
            </span>
            <span className={dark ? 'text-white text-xl font-bold tracking-tight mb-2' : 'text-[#121212] text-xl font-bold tracking-tight mb-2'} style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {o.title}
            </span>
            <span className={dark ? 'text-white/55 text-sm flex-1' : 'text-gray-500 text-sm flex-1'} style={{ fontFamily: 'Manrope' }}>
              {o.desc}
            </span>
            <span className="flex items-center justify-between mt-4">
              <span className={
                active
                  ? 'inline-flex items-center gap-1.5 text-[#4A6B53] text-xs font-semibold bg-[#4A6B53]/10 px-3 py-1 rounded-full'
                  : dark
                    ? 'inline-flex items-center gap-1.5 text-amber-300/90 text-xs font-semibold bg-amber-500/10 px-3 py-1 rounded-full'
                    : 'inline-flex items-center gap-1.5 text-[#4A6B53] text-xs font-semibold bg-[#4A6B53]/10 px-3 py-1 rounded-full'
              }>
                {active
                  ? L('Pārvaldīt veikalu', 'Manage shop', 'Управлять')
                  : (<>{o.id === 'product' && <Sparkles className="w-3 h-3" />} {o.note}</>)}
              </span>
              <ArrowRight className={
                active
                  ? 'w-4 h-4 text-[#4A6B53] opacity-70 group-hover:opacity-100 transition-opacity'
                  : dark
                    ? 'w-4 h-4 text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity'
                    : 'w-4 h-4 text-[#D97736] opacity-0 group-hover:opacity-100 transition-opacity'
              } />
            </span>
          </motion.button>
        );
      })}
    </div>
  );
}
